import config from "../utils/config";
import axios from "axios";
import {
  gtag_report_conversion_addToCart,
  gtag_report_conversion_subscribe,
  gtag_report_conversion_signup,
} from "./googleConversionFunctions";

// dayjs
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
// Import the plugin
dayjs.extend(relativeTime);

// export function validateURL(value) {
//   return /(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*))/.test(
//     value
//   );
// }

export function validateURL(url) {
  try {
    const parsedUrl = new URL(url);

    // Ensure the hostname contains at least one dot (.)
    if (!parsedUrl.hostname.includes(".")) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
}

export function validateEmail(email) {
  const regexp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexp.test(email);
}

// Validate the inputted URL
export function validateURLInput(finderInput) {
  let validatedURL = finderInput;

  // Check if input got https:// twice
  if (validatedURL.startsWith("https://https://")) {
    validatedURL = validatedURL.substring(8);
  }

  // Add https:// if it is missing. Allow http too.
  if (!/^https?:\/\//i.test(validatedURL)) {
    validatedURL = "https://" + validatedURL;
  }

  return validatedURL;
}

export function isOverflowing(el) {
  if (el && el.style) {
    var originalOverflow = el.style.overflow;

    // Make the overflow temporarily hidden
    el.style.overflow = "hidden";

    var isOverflowing =
      el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;

    // Set it back to what it was
    el.style.overflow = originalOverflow;

    return isOverflowing;
  } else {
    return false;
  }
}

// Binary search text resize
export const resizeText = ({
  element,
  minSize = 5,
  maxSize = 600,
  step = 1,
  unit = "px",
  fontAdjustment = 0,
}) => {
  let el = element;

  let i = maxSize / 2;
  let overflow = false;
  let leftBound = minSize;
  let rightBound = maxSize;

  const parent = el.parentNode;

  do {
    el.style.fontSize = `${i}${unit}`;
    overflow = isOverflowing(parent);

    if (overflow) {
      rightBound = i;
    } else {
      leftBound = i;
    }

    i = leftBound + (rightBound - leftBound) / 2;
  } while (rightBound - leftBound > 2);

  let correctFontSize = Math.floor(i) - step - 5 + fontAdjustment; // the -5 is to make sure the size is normalized across browsers. It is important.

  // Round down to the nearest integer
  correctFontSize = Math.floor(correctFontSize);

  // revert to last state where no overflow happened
  el.style.fontSize = `${correctFontSize}${unit}`;

  return correctFontSize;
};

export const resizeMultipleTextElements = ({
  elements,
  minSize = 5,
  maxSize = 1000,
  step = 1,
  unit = "px",
  fontAdjustment = 0,
}) => {
  elements.forEach((el) => {
    resizeText({
      element: el,
      minSize: minSize,
      maxSize: maxSize,
      step: step,
      unit: unit,
      fontAdjustment: fontAdjustment,
    });
  });
};

// SOCIAL MEDIA OAUTH FUNCTIONS

/**
 * On local dev, OAuth redirect_uri must match the protocol you are actually using
 * (https://localhost:2001 when using npm run dev:https). Static config often lists http://.
 */
export function getOAuthRedirectUri(configuredRedirect, path = "/profile") {
  if (typeof window === "undefined") {
    return configuredRedirect;
  }
  const { hostname, origin } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `${origin}${path}`;
  }
  return configuredRedirect;
}

/** Target platform for a post — keep in sync with PostToSocialMediaButton / scheduling. */
export function getPostTargetPlatform(post) {
  if (!post) {
    return "pinterest";
  }
  if (post.isAIGeneratedFromReference && post.platform) {
    return post.platform;
  }
  if (!post.template) {
    return "pinterest";
  }
  return post.template.platform || "pinterest";
}

// Platform configurations for OAuth
export const getSocialMediaConfig = (platform) => {
  const configs = {
    pinterest: {
      authUrl: "https://www.pinterest.com/oauth/",
      clientId: config.pinterest.appId,
      redirectUri: getOAuthRedirectUri(config.pinterest.redirectURL),
      scope:
        "boards:read,boards:read_secret,pins:read,pins:read_secret,boards:write,boards:write_secret,pins:write,pins:write_secret,user_accounts:read,ads:read,catalogs:read,catalogs:write",
      statePrefix: "pinterest_",
      apiEndpoint: "/api/pinterestAPI",
    },
    x: {
      authUrl: "https://x.com/i/oauth2/authorize",
      clientId: config.x?.clientId || "placeholder",
      redirectUri: getOAuthRedirectUri(
        config.x?.redirectURL || config.pinterest.redirectURL
      ),
      scope: "tweet.read tweet.write users.read offline.access media.write",
      statePrefix: "x_",
      apiEndpoint: "/api/socialMedia/x",
    },
    instagram: {
      authUrl: "https://www.instagram.com/oauth/authorize",
      clientId: config.instagram?.appId || "placeholder",
      redirectUri: getOAuthRedirectUri(
        config.instagram?.redirectURL || config.pinterest.redirectURL
      ),
      scope: "instagram_business_basic,instagram_business_content_publish",
      statePrefix: "instagram_",
      apiEndpoint: "/api/socialMedia/instagram",
    },
    facebook: {
      authUrl: "https://www.facebook.com/v23.0/dialog/oauth",
      clientId: config.facebook?.appId || "placeholder",
      redirectUri: getOAuthRedirectUri(
        config.facebook?.redirectURL || config.pinterest.redirectURL
      ),
      scope:
        "public_profile,email,business_management,pages_show_list,pages_manage_posts,pages_read_engagement",
      statePrefix: "facebook_",
      apiEndpoint: "/api/socialMedia/facebook",
    },
    linkedin: {
      authUrl: "https://www.linkedin.com/oauth/v2/authorization",
      clientId: config.linkedin?.clientId || "placeholder",
      redirectUri: getOAuthRedirectUri(
        config.linkedin?.redirectURL || config.pinterest.redirectURL
      ),
      scope: "openid profile email w_member_social",
      statePrefix: "linkedin_",
      apiEndpoint: "/api/socialMedia/linkedin",
    },
  };

  return configs[platform.toLowerCase()];
};

// Generic social media OAuth initiation
export const startSocialMediaOAuth = async (
  platform,
  useAlert = false,
  customRedirectUrl = null,
  posts = null,
  createDraftAction = null,
  profileUsername = null
) => {
  console.log(`Running ${platform} OAuth`);

  const platformConfig = getSocialMediaConfig(platform);
  if (!platformConfig) {
    console.error(`Unsupported platform: ${platform}`);
    return;
  }

  // Save posts to drafts before starting OAuth if posts are provided
  if (posts && posts.length > 0 && createDraftAction) {
    try {
      console.log(`Saving ${posts.length} posts to drafts before OAuth...`);
      await createDraftAction(posts, "Autosave");
      console.log("Posts saved to drafts successfully");
    } catch (error) {
      console.error("Failed to save posts to drafts before OAuth:", error);
      // Continue with OAuth even if draft save fails
    }
  }

  const runOAuth = async (
    stateCheck,
    codeChallenge = null,
    codeChallengeMethod = null
  ) => {
    // Build OAuth URL based on platform
    let oauthUrl;

    if (platform === "pinterest") {
      // Pinterest uses a specific URL format
      oauthUrl = `${platformConfig.authUrl}?client_id=${
        platformConfig.clientId
      }&redirect_uri=${encodeURI(
        platformConfig.redirectUri
      )}&response_type=code&scope=${platformConfig.scope}&state=${stateCheck}`;
    } else if (platform === "x") {
      // Twitter OAuth 2.0 format with PKCE S256
      const challenge = codeChallenge || (await generateCodeChallenge());
      const method = codeChallengeMethod || "S256";

      // Clear any existing X OAuth state before starting new flow
      clearXOAuthState();

      // Add timestamp to prevent URL caching
      const timestamp = Date.now();

      oauthUrl = `${platformConfig.authUrl}?response_type=code&client_id=${
        platformConfig.clientId
      }&redirect_uri=${encodeURI(platformConfig.redirectUri)}&scope=${encodeURI(
        platformConfig.scope
      )}&state=${stateCheck}&code_challenge=${challenge}&code_challenge_method=${method}&t=${timestamp}`;
    } else if (platform === "facebook") {
      // Facebook OAuth format
      oauthUrl = `${platformConfig.authUrl}?client_id=${
        platformConfig.clientId
      }&redirect_uri=${encodeURI(platformConfig.redirectUri)}&scope=${encodeURI(
        platformConfig.scope
      )}&response_type=code&state=${stateCheck}`;
    } else if (platform === "linkedin") {
      // LinkedIn OAuth format
      oauthUrl = `${platformConfig.authUrl}?response_type=code&client_id=${
        platformConfig.clientId
      }&redirect_uri=${encodeURI(platformConfig.redirectUri)}&scope=${encodeURI(
        platformConfig.scope
      )}&state=${stateCheck}`;
    } else if (platform === "instagram") {
      // Instagram Business OAuth format
      oauthUrl = `${platformConfig.authUrl}?client_id=${
        platformConfig.clientId
      }&redirect_uri=${encodeURI(platformConfig.redirectUri)}&scope=${encodeURI(
        platformConfig.scope
      )}&response_type=code&state=${stateCheck}&force_reauth=true`;
    } else {
      console.error(`OAuth URL generation not implemented for ${platform}`);
      return;
    }

    console.log(`Opening ${platform} OAuth URL:`, oauthUrl);

    window.open(oauthUrl, config.configName === "Live" ? "_self" : "_blank");
  };

  // Generate state check with platform prefix
  const stateCheck = `${platformConfig.statePrefix}${randomString(
    16,
    "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ"
  )}`;

  const fullPath =
    customRedirectUrl || window.location.pathname + window.location.search;

  if (useAlert) {
    const profileInfo = profileUsername ? ` (${profileUsername})` : "";
    const userResponse = window.confirm(
      `We were unable to connect to your ${
        platformConfig.name || platform
      } profile${profileInfo}. Would you like to reauthorize it now?`
    );

    if (!userResponse) {
      console.log(`User canceled ${platform} reauthorization.`);
      return;
    }
  }

  // Record state check based on platform
  const apiEndpoint = "/api/socialMedia/recordOAuthStateCheck";

  const requestBody = {
    platform: platform,
    stateCheck: stateCheck,
    redirectUrl: fullPath,
    oauthRedirectUri: platformConfig.redirectUri || undefined,
  };

  axios
    .post(apiEndpoint, requestBody)
    .then(async (response) => {
      console.log(`Successfully recorded ${platform} state check`);

      // For Twitter, use the dynamically generated code challenge
      if (platform === "x" && response.data.codeChallenge) {
        await runOAuth(
          stateCheck,
          response.data.codeChallenge,
          response.data.codeChallengeMethod
        );
      } else {
        await runOAuth(stateCheck);
      }
    })
    .catch((err) => {
      console.error(`Unable to record ${platform} state check: ` + err);
    });
};

// Backward compatibility: Pinterest OAuth function
export const startPinterestOAuth = (
  useAlert = false,
  profileUsername = null
) => {
  console.log("Running Pinterest oAuth");

  const runPinterestOAuth = (stateCheck) => {
    let fullScope =
      "boards:read,boards:read_secret,pins:read,pins:read_secret,boards:write,boards:write_secret,pins:write,pins:write_secret,user_accounts:read,ads:read,catalogs:read,catalogs:write";
    let testScope = "boards:read,pins:read,user_accounts:read,catalogs:read";

    window.open(
      `https://www.pinterest.com/oauth/?client_id=${
        config.pinterest.appId
      }&redirect_uri=${encodeURI(
        config.pinterest.redirectURL
      )}&response_type=code
    &scope=${fullScope} 
    &state=${stateCheck}`,
      config.configName == "Live" ? "_self" : "_blank"
    );
  };

  let stateCheck = randomString(
    16,
    "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ"
  );

  const fullPath = window.location.pathname + window.location.search;

  if (useAlert) {
    // Show alert with Cancel and Reauthorize now options
    const profileInfo = profileUsername ? ` (${profileUsername})` : "";
    const userResponse = window.confirm(
      `We were unable to connect to your Pinterest profile${profileInfo}. Would you like to reauthorize it now?`
    );

    if (!userResponse) {
      console.log("User canceled reauthorization.");
      return; // Exit if the user cancels
    }
  }

  // If the user agrees or `useAlert` is false, proceed with the OAuth process
  axios
    .post("/api/pinterestAPI/recordPinterestStateCheck", {
      stateCheck: stateCheck,
      redirectUrl: fullPath,
    })
    .then(() => {
      console.log("Successfully recorded Pinterest state check");
      runPinterestOAuth(stateCheck);
    })
    .catch((err) => {
      console.error("Unable to record Pinterest state check: " + err);
    });
};

// Get active social media profile utility
export function getActiveSocialMediaProfile(
  authorizedSocialMediaProfiles,
  platform
) {
  let activeProfileUsername = null;

  if (
    authorizedSocialMediaProfiles &&
    authorizedSocialMediaProfiles[platform] &&
    authorizedSocialMediaProfiles[platform].length > 0
  ) {
    authorizedSocialMediaProfiles[platform].forEach((profile) => {
      if (profile.isActive) {
        activeProfileUsername = profile.username;
      }
    });
  }

  return activeProfileUsername;
}

// Pinterest-specific convenience functions
// DEPRECATED: Pinterest tokens are now managed server-side
export const setPinAccessToken = (rawAccessToken) => {
  console.warn(
    "setPinAccessToken is deprecated - Pinterest tokens are managed server-side"
  );
  // No longer storing tokens in sessionStorage
};

export const setPinRefreshToken = (
  refresh_token,
  refresh_token_expires_in,
  refresh_token_expires_at
) => {
  console.warn(
    "setPinRefreshToken is deprecated - Pinterest tokens are managed server-side"
  );
  // No longer storing tokens in sessionStorage
};

// Get Pinterest tokens using new generic keys
// DEPRECATED: Pinterest tokens are now managed server-side
export const getPinAccessToken = () => {
  // Pinterest tokens are now fetched from database on the backend
  console.warn(
    "getPinAccessToken is deprecated - Pinterest tokens are managed server-side"
  );
  return null;
};

export const getPinRefreshToken = () => {
  // Pinterest tokens are now fetched from database on the backend
  console.warn(
    "getPinRefreshToken is deprecated - Pinterest tokens are managed server-side"
  );
  return null;
};

export const flattenColor = (fgCol, bgCol, opacity) => {
  //const fgCol = { r: 255, g: 0, b: 0 };
  //const bgCol = { r: 0, g: 0, b: 0 };
  //const opacity = 0.5;

  const flattenedColor = {
    r: opacity * fgCol.r + (1 - opacity) * bgCol.r,
    g: opacity * fgCol.g + (1 - opacity) * bgCol.g,
    b: opacity * fgCol.b + (1 - opacity) * bgCol.b,
  };

  return flattenedColor;
};

// Get the size of the browser window
export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

// Start Shopify OAuth using a store name like post-generator-development
export const startShopifyOAuth = (myShopName) => {
  // Start OAuth with Shopify
  console.log("Start OAuth with Shopify");
  let redirectURI = config.shopify.redirectURL;

  let alphaNumericValues =
    "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

  let stateCheck = `STATEShopify-${randomString(6, alphaNumericValues)}`;

  localStorage.setItem("ShopifyStateCheck", stateCheck);

  stateCheck = encodeURIComponent(stateCheck);

  window.open(
    `https://${myShopName}.myshopify.com/admin/oauth/authorize?client_id=${config.shopify.apiKey}&scope=read_products&redirect_uri=${redirectURI}&state=${stateCheck}`,
    config.configName == "Live" ? "_self" : "_blank"
  );
};

function randomString(length, chars) {
  var result = "";
  for (var i = length; i > 0; --i)
    result += chars[Math.floor(Math.random() * chars.length)];
  return result;
}

export const trackAddToCart = async (planName, amount) => {
  console.log("Add to cart event");

  // cast amount to an number
  amount = parseFloat(amount);

  if (
    config.configName == "Live" &&
    JSON.parse(localStorage.getItem("cookiesConsent")) === true
  ) {
    // Get users IP address
    fetch("https://api.ipify.org?format=json")
      .then((results) => results.json())
      .then((data) => {
        let ip = data.ip;

        //Send FB event: add to cart
        // axios.post("/api/facebook/addToCart", {
        //   planName,
        //   amount,
        //   url: window.location.href,
        //   ipAddress: ip,
        // });

        // TikTok add to cart event
        // axios
        //   .post("/api/tiktok/addToCart", {
        //     planName,
        //     amount,
        //     url: window.location.href,
        //     ipAddress: ip,
        //     userAgent: navigator.userAgent,
        //     referrer: document.referrer,
        //   })
        //   .catch((err) => {
        //     console.error("Unable to send TikTok add to cart event: " + err);
        //   });

        // Pinterest add to cart event
        // pintrk("track", "addtocart", {
        //   value: amount / 4,
        //   order_quantity: 1,
        //   currency: "USD",
        //   line_items: [
        //     {
        //       product_name: planName,
        //       product_price: amount,
        //     },
        //   ],
        // });

        // GoogleAds addToCart event
        //gtag_report_conversion_addToCart(window.location.href, amount / 4);
      });
  }
};

// Track users subscribe purchase on ad tracking
export const trackSubscribe = async (
  planName = "unknown",
  amount = 0,
  userEmail
) => {
  console.log("Subscribe event");

  // Cast amount to a number
  amount = parseFloat(amount);

  if (
    config.configName == "Live"
    //&& JSON.parse(localStorage.getItem("cookiesConsent")) === true
  ) {
    // Get users IP address
    fetch("https://api.ipify.org?format=json")
      .then((results) => results.json())
      .then((data) => {
        let ip = data.ip;

        //   //Send FB event: subscribe
        //   axios.post("/api/facebook/subscribe", {
        //     planName,
        //     amount,
        //     url: window.location.href,
        //     ipAddress: ip,
        //   });

        //   // TikTok subscribe event
        //   axios
        //     .post("/api/tiktok/subscribe", {
        //       planName,
        //       amount,
        //       url: window.location.href,
        //       ipAddress: ip,
        //       userAgent: navigator.userAgent,
        //       referrer: document.referrer,
        //     })
        //     .catch((err) => {
        //       console.error("Unable to send TikTok subscribe event: " + err);
        //     });

        //   // Pinterest checkout event
        //   pintrk("track", "checkout", {
        //     value: amount,
        //     order_quantity: 1,
        //     currency: "USD",
        //     line_items: [
        //       {
        //         product_name: planName,
        //         product_price: amount,
        //       },
        //     ],
        //   });

        //   // GoogleAds subscribe event
        //   gtag_report_conversion_subscribe(
        //     window.location.href,
        //     amount,
        //     userEmail
        //   );

        //   // Reddit track subscribe event
        //   rdt("track", "Purchase", {
        //     currency: "USD",
        //     itemCount: 1,
        //     value: amount,
        //     products: [
        //       {
        //         id: planName,
        //         name: planName,
        //         category: "subscriptions",
        //       },
        //     ],
        //   });

        //   //Twitter track subscribe event
        //   twq("event", "tw-pdibw-pdibx", {
        //     value: amount,
        //     currency: "USD",
        //     conversion_id: userEmail,
        //     email_address: userEmail,
        //   });

      });
  }
};

export const trackLifetimePurchase = async (
  planName = "unknown",
  amount = 0,
  userEmail
) => {
  console.log("Lifetime purchase event");

  // Cast amount to a number
  amount = parseFloat(amount);

  if (
    config.configName == "Live"
    //&& JSON.parse(localStorage.getItem("cookiesConsent")) === true
  ) {
    // Get users IP address
    fetch("https://api.ipify.org?format=json")
      .then((results) => results.json())
      .then((data) => {
        let ip = data.ip;

      });
  }
};

export const trackSignUp = async (email, ip) => {
  console.log("Sign up event");

  if (
    config.configName == "Live"
    //&& JSON.parse(localStorage.getItem("cookiesConsent")) === true
  ) {
    // Get users IP address
    fetch("https://api.ipify.org?format=json")
      .then((results) => results.json())
      .then((data) => {
        let ip = data.ip;

        // //Send FB event: complete registration
        // axios.post("/api/facebook/completeRegistration", {
        //   url: window.location.href,
        //   ipAddress: ip,
        // });

        // // TikTok signup event
        // axios
        //   .post("/api/tiktok/signup", {
        //     email: email,
        //     url: window.location.href,
        //     ipAddress: ip,
        //     userAgent: navigator.userAgent,
        //     referrer: document.referrer,
        //   })
        //   .catch((err) => {
        //     console.error("Unable to send TikTok signup event: " + err);
        //   });

        // // Pinterest signup event
        // pintrk("track", "signup");

        // // GoogleAds signup event
        // gtag_report_conversion_signup(window.location.href, email);

        // // Reddit track signup event
        // rdt("track", "SignUp", {
        //   currency: "USD",
        //   value: 1,
        //   products: [
        //     {
        //       id: "Content Generator Signup",
        //       name: "Content Generator Signup",
        //       category: "signups",
        //     },
        //   ],
        // });

      });
  }
};

export function getNewPostFileName(title) {
  // Replace all non-alphanumeric characters with a dash
  let sanitizedTitle = title.replace(/[^a-z0-9]/gi, "-");

  // Remove all emojis
  sanitizedTitle = sanitizedTitle.replace(/[\u{1F600}-\u{1F64F}]/gu, "");

  let randomString = Math.floor(Math.random() * 9999999 + 1000000);

  let titleMaxLength = 100;
  if (sanitizedTitle.length > titleMaxLength) {
    sanitizedTitle = sanitizedTitle.substring(0, titleMaxLength);
  }

  return `${sanitizedTitle}-${randomString}.jpeg`;
}

export function dataURLtoFile(dataurl, filename) {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
}

export function getNonEmptyPostOverrides(pinOverrides) {
  let nonEmpty = {};
  if (pinOverrides) {
    Object.keys(pinOverrides).forEach((key) => {
      if (pinOverrides[key] != "") {
        nonEmpty[key] = pinOverrides[key];
      }
    });
  }
  return nonEmpty;
}

// generate a 6 character hexadecimal string
export const generateElementId = () => {
  return Math.random().toString(16).substr(2, 6);
};

// Detect if user is using a Safari browser or safari mobile
export const isSafari = () => {
  var ua = navigator.userAgent.toLowerCase();
  if (ua.indexOf("safari") != -1) {
    if (ua.indexOf("chrome") > -1) {
      return false;
    } else {
      //console.log("User is using Safari");
      return true;
    }
  }

  return false;
};

// Detect if mobile is being used
export const isMobile = () => {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    //console.log("User is using mobile");
    return true;
  }
  return false;
};

export function toTitleCase(str) {
  if (typeof str == "undefined") {
    return "";
  }

  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

export function isRenderingForDesign() {
  let renderForDesign =
    typeof window != "undefined"
      ? window.location.href.includes("template-builder")
      : false;

  return renderForDesign;
}

export function getDateStringAsMonthYear(dateString) {
  // Get date as DD MM YYYY
  let date = new Date(dateString);
  let day = date.getDate();
  let month = date.toLocaleString("default", { month: "long" });
  let year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

export function replaceLinksInBlog(str) {
  if (!str) {
    return str;
  }

  //Find any links with http or https that start with blog.contentgenerator.io and replace with ${config.baseURL}/blog
  //let regex = /https?:\/\/blog.contentgenerator.io\/*/gi;
  let regex =
    /(http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:\/~+#-]*[\w@?^=%&\/~+#-])?/gi;
  // let regex = new RegExp(`https?:\/\/blog.contentgenerator.io`, "g");

  let matches = str.match(regex);

  if (matches) {
    matches.forEach((match) => {
      if (
        match.includes("blog.contentgenerator.io") &&
        !match.includes("wp-content") // Dont match on uploades (images, docs, etc)
      ) {
        let newLink = match.replace(
          "https://blog.contentgenerator.io",
          `${config.baseURL}/blog`
        );

        newLink = newLink.replace(
          "http://blog.contentgenerator.io",
          `${config.baseURL}/blog`
        );

        str = str.replace(match, newLink);
      }
    });
  }

  return str;
}

export function removeUnicode(str) {
  if (str) {
    if (str.includes("&#8211;")) {
      return str.replace("&#8211;", "-");
    }
    if (str.includes("&#8217;")) {
      return str.replace("&#8217;", "'");
    }
    if (str.includes("&#8220;")) {
      return str.replace("&#8220;", '"');
    }
    if (str.includes("&#8221;")) {
      return str.replace("&#8221;", '"');
    }
    if (str.includes("&#8230;")) {
      return str.replace("&#8230;", "...");
    }
    if (str.includes("&#038;")) {
      return str.replace("&#038;", "&");
    }
    if (str.includes("&#039;")) {
      return str.replace("&#039;", "'");
    }
    if (str.includes("&#160;")) {
      return str.replace("&#160;", " ");
    }
    if (str.includes("&amp;")) {
      return str.replace("&amp;", "&");
    }
  }

  return str;
}

// Give the post a color and a fileName
export function preparePostForRendering(post, designColors, userCredentials = null) {
  let smallPost = post;
  smallPost.fileName = getNewPostFileName(post.title);

  // Use the post's existing color if available, otherwise fall back to design colors lookup
  if (post.color) {
    smallPost.color = post.color;
  } else if (
    designColors &&
    post.colorIndex !== undefined &&
    designColors[post.colorIndex]
  ) {
    smallPost.color = designColors[post.colorIndex].color;
  }

  // Add whitelisting status if available (for image loading optimization)
  if (userCredentials?.hasWhitelistedWebsite) {
    smallPost.hasWhitelistedWebsite = true;
  }

  return smallPost;
}

// Platform-specific character limits and constraints
export const PLATFORM_CONSTRAINTS = {
  pinterest: {
    title: { min: 10, max: 100, optimal: 40 },
    description: { min: 200, max: 500, optimal: 350 },
    features: ["hashtags", "emojis", "links"],
    style: "engaging and visual-focused",
  },
  x: {
    title: { min: 10, max: 100, optimal: 40 },
    description: { min: 10, max: 280, optimal: 240 },
    features: ["hashtags", "mentions", "threads"],
    style: "concise and conversational",
  },
  facebook: {
    title: { min: 10, max: 100, optimal: 40 },
    description: { min: 50, max: 2000, optimal: 400 },
    features: ["hashtags", "emojis", "links", "mentions"],
    style: "engaging and community-focused",
  },
  instagram: {
    title: { min: 10, max: 100, optimal: 40 },
    description: { min: 50, max: 2200, optimal: 500 },
    features: ["hashtags", "emojis", "mentions", "stories"],
    style: "visual-first and lifestyle-oriented",
  },
  linkedin: {
    title: { min: 10, max: 100, optimal: 40 },
    description: { min: 100, max: 3000, optimal: 600 },
    features: ["professional hashtags", "mentions", "articles"],
    style: "professional and value-driven",
  },
};

// Get platform-specific AI instructions
export function getPlatformAIInstructions(platform, aiSpecification) {
  const constraints =
    PLATFORM_CONSTRAINTS[platform] || PLATFORM_CONSTRAINTS.pinterest;

  let baseInstructions = "";

  // Add AI specification context with better formatting
  if (aiSpecification) {
    baseInstructions += `You are an expert social media content creator. Write content with the following style: ${aiSpecification}.\n\n`;
  }

  // Add platform-specific context
  switch (platform) {
    case "pinterest":
      baseInstructions +=
        "Create Pinterest-optimized content that is visual-focused and discovery-driven. ";
      break;
    case "x":
      baseInstructions +=
        "Create Twitter/X content that is concise, conversational, and engaging. ";
      break;
    case "facebook":
      baseInstructions +=
        "Create Facebook content that encourages community engagement and sharing. ";
      break;
    case "instagram":
      baseInstructions +=
        "Create Instagram content that is visual-first and lifestyle-oriented. ";
      break;
    case "linkedin":
      baseInstructions +=
        "Create LinkedIn content that is professional, value-driven, and industry-focused. ";
      break;
    default:
      baseInstructions += "Create engaging social media content. ";
  }

  return baseInstructions;
}

export function createPlatformAwareTitlePrompt(
  title,
  platform = "pinterest",
  language = "English",
  aiKeywords = "",
  aiTone = "",
  aiSpecification = "",
  includeHashtags = true
) {
  // trim any extra empty end of line characters
  aiKeywords = aiKeywords.trim();

  const constraints =
    PLATFORM_CONSTRAINTS[platform] || PLATFORM_CONSTRAINTS.pinterest;
  const baseInstructions = getPlatformAIInstructions(platform, aiSpecification);

  let prompt = "";

  // Start the prompt with a random number between 1 and 1000
  let randomNumber = Math.floor(Math.random() * 1000 + 1);
  prompt = `${randomNumber} `;

  // Add base instructions
  prompt += baseInstructions;

  // Rewrite text
  if (aiKeywords == "") {
    prompt += `Rewrite this ${platform} title in a completely different way. `;
    prompt += `Target around ${constraints.title.optimal} characters. `;
    prompt += `CRITICAL: Your response MUST NOT EXCEED ${constraints.title.max} characters. `;
    prompt += `Make it ${constraints.style}. `;
    prompt += `Write in ${language}. `;
    prompt += `Only output the new text. `;

    if (aiTone != "") {
      prompt += `Use a ${aiTone} tone. `;
    }

    prompt += `The current title is: "${title}"`;
  } else {
    // split on a new line
    let keywordArray = aiKeywords.split("\n");
    let randomKeyword =
      keywordArray[Math.floor(Math.random() * keywordArray.length)];

    prompt += `Create a ${platform} title using this exact keyword phrase: "${randomKeyword}". `;
    prompt += `Target around ${constraints.title.optimal} characters. `;
    prompt += `CRITICAL: Your response MUST NOT EXCEED ${constraints.title.max} characters. `;
    prompt += `Make it ${constraints.style}. `;
    prompt += `Write in ${language}. `;
    prompt += `Only output the text. `;

    if (aiTone != "") {
      prompt += `Use a ${aiTone} tone. `;
    }
  }

  return prompt;
}

// Legacy function for backward compatibility
export function createRewriteTitlePrompt(
  title,
  language,
  aiKeywords = "",
  aiTone = ""
) {
  return createPlatformAwareTitlePrompt(
    title,
    "pinterest",
    language,
    aiKeywords,
    aiTone
  );
}

export function createPlatformAwareDescriptionPrompt(
  description,
  platform = "pinterest",
  language = "English",
  aiKeywords = "",
  aiTone = "",
  aiSpecification = "",
  includeHashtags = true
) {
  // trim any extra empty end of line characters
  aiKeywords = aiKeywords.trim();

  // Add a full stop if there isn't one and it's not X (which might not need it)
  if (!description.endsWith(".") && platform !== "x") {
    description = description + ".";
  }

  const constraints =
    PLATFORM_CONSTRAINTS[platform] || PLATFORM_CONSTRAINTS.pinterest;
  const baseInstructions = getPlatformAIInstructions(platform, aiSpecification);

  let prompt = baseInstructions;

  // Rewrite text
  if (aiKeywords == "") {
    prompt += `Rewrite this ${platform} description in a completely different way. `;
    prompt += `Target around ${constraints.description.optimal} characters. `;
    prompt += `CRITICAL: Your response MUST NOT EXCEED ${constraints.description.max} characters. `;
    prompt += `Make it ${constraints.style}. `;
    prompt += `Write in ${language}. `;
    prompt += `Only output the text. `;

    if (aiTone != "") {
      prompt += `Use a ${aiTone} tone. `;
    }

    prompt += `This is the text: "${description}"`;
  } else {
    // split on a new line
    let keywordArray = aiKeywords.split("\n");
    let commaSeparatedKeywords = keywordArray.join(", ");

    prompt += `Create a new ${platform} description using one or more of these keyword phrases: ${commaSeparatedKeywords}. `;
    prompt += `Target around ${constraints.description.optimal} characters. `;
    prompt += `CRITICAL: Your response MUST NOT EXCEED ${constraints.description.max} characters. `;
    prompt += `Make it ${constraints.style}. `;
    prompt += `Write in ${language}. `;
    prompt += `Only output the text. `;

    if (aiTone != "") {
      prompt += `Use a ${aiTone} tone. `;
    }
  }

  // Add hashtag instruction if enabled - stronger emphasis
  if (constraints.features && constraints.features.includes("hashtags")) {
    if (includeHashtags) {
      prompt +=
        " IMPORTANT: You MUST include 3-5 relevant hashtags at the end of your response. This is required.";
    } else {
      prompt +=
        " IMPORTANT: You MUST NOT include any hashtags in your response.";
    }
  }
  return prompt;
}

// Legacy function for backward compatibility
export function createRewriteDescriptionPrompt(
  description,
  language,
  aiKeywords = "",
  aiTone = ""
) {
  return createPlatformAwareDescriptionPrompt(
    description,
    "pinterest",
    language,
    aiKeywords,
    aiTone
  );
}

export function createPlatformAwareContentFromTitlePrompt(
  title,
  platform = "pinterest",
  language = "English",
  aiKeywords = "",
  aiTone = "",
  aiSpecification = "",
  includeHashtags = true
) {
  let prompt = "";

  // trim any extra empty end of line characters
  aiKeywords = aiKeywords.trim();

  const constraints =
    PLATFORM_CONSTRAINTS[platform] || PLATFORM_CONSTRAINTS.pinterest;
  const baseInstructions = getPlatformAIInstructions(platform, aiSpecification);

  prompt = baseInstructions;

  // Generate content from title
  if (aiKeywords == "") {
    prompt += `Write a ${platform} description for this post with the title "${title}". `;
    prompt += `Target around ${constraints.description.optimal} characters. `;
    prompt += `CRITICAL: Your response MUST NOT EXCEED ${constraints.description.max} characters. `;
    prompt += `Make it ${constraints.style}. `;
    prompt += `Write in ${language}. `;
    prompt += `Only output the text. `;

    if (aiTone != "") {
      prompt += `Use a ${aiTone} tone. `;
    }
  } else {
    // Write a description using a keyword phrase
    let keywordArray = aiKeywords.split("\n");
    let commaSeparatedKeywords = keywordArray.join(", ");

    prompt += `Create a new ${platform} description using one or more of these keyword phrases: ${commaSeparatedKeywords}. `;
    prompt += `Base it on this title: "${title}". `;
    prompt += `Target around ${constraints.description.optimal} characters. `;
    prompt += `CRITICAL: Your response MUST NOT EXCEED ${constraints.description.max} characters. `;
    prompt += `Make it ${constraints.style}. `;
    prompt += `Write in ${language}. `;
    prompt += `Only output the text. `;

    if (aiTone != "") {
      prompt += `Use a ${aiTone} tone. `;
    }
  }

  // Add hashtag instruction if enabled - stronger emphasis
  if (constraints.features && constraints.features.includes("hashtags")) {
    if (includeHashtags) {
      prompt +=
        " IMPORTANT: You MUST include 3-5 relevant hashtags at the end of your response. This is required.";
    } else {
      prompt +=
        " IMPORTANT: You MUST NOT include any hashtags in your response.";
    }
  }
  return prompt;
}

// Comprehensive platform-aware content generation
export function createPlatformAwarePostContentPrompt(
  postData,
  platform = "pinterest",
  language = "English",
  aiKeywords = "",
  aiTone = "",
  aiSpecification = "",
  includeHashtags = true
) {
  const constraints =
    PLATFORM_CONSTRAINTS[platform] || PLATFORM_CONSTRAINTS.pinterest;
  const baseInstructions = getPlatformAIInstructions(platform, aiSpecification);

  let prompt = baseInstructions;

  // Add content context
  prompt += `Create engaging ${platform} content based on this information:\n`;
  if (postData.title) {
    prompt += `Title: ${postData.title}\n`;
  }
  if (postData.description) {
    prompt += `Description: ${postData.description}\n`;
  }

  prompt += `\nGenerate optimized content for ${platform} that:\n`;
  prompt += `- Is ${constraints.style}\n`;
  prompt += `- Target around ${constraints.description.optimal} characters\n`;

  if (constraints.features.includes("emojis")) {
    prompt += `- Uses appropriate emojis\n`;
  }

  prompt += `- Is written in ${language}\n`;

  if (aiTone) {
    prompt += `- Uses a ${aiTone} tone\n`;
  }

  if (aiKeywords.trim()) {
    const keywordArray = aiKeywords.trim().split("\n");
    const commaSeparatedKeywords = keywordArray.join(", ");
    prompt += `- Incorporates these keywords naturally: ${commaSeparatedKeywords}\n`;
  }

  // Hashtag instruction - place at end for stronger emphasis
  if (constraints.features.includes("hashtags")) {
    if (includeHashtags) {
      prompt += `\nIMPORTANT: You MUST include 3-5 relevant hashtags at the end of your post. This is required.\n`;
    } else {
      prompt += `\nIMPORTANT: You MUST NOT include any hashtags in your response.\n`;
    }
  }

  prompt += `\nReturn only ONE final post content version, no explanations, no alternatives, no variations.`;

  return prompt;
}

// Legacy function for backward compatibility
export function createWriteDescriptionFromTitlePrompt(
  title,
  language,
  aiKeywords = "",
  aiTone = ""
) {
  return createPlatformAwareContentFromTitlePrompt(
    title,
    "pinterest",
    language,
    aiKeywords,
    aiTone
  );
}

export async function loadCustomFont(
  fontName,
  fontUrl,
  successFunction = null,
  maxRetries = 3
) {
  // Check if this is being executed in a browser environment
  if (typeof document === "undefined" || typeof fontUrl == "undefined") {
    return;
  }

  // Validate font URL
  if (!fontUrl || typeof fontUrl !== "string") {
    console.error("Invalid font URL provided:", fontUrl);
    return;
  }

  let alreadyHasFont = false;

  for (let font of document.fonts.values()) {
    if (font.family === fontName) {
      alreadyHasFont = true;
      break;
    }
  }

  if (alreadyHasFont) return;

  let fontExtension = fontUrl.split(".").pop()?.split("?")[0]; // Remove query parameters
  let fontFormat = "";

  // Find the font format
  switch (fontExtension?.toLowerCase()) {
    case "woff":
      fontFormat = "woff";
      break;
    case "woff2":
      fontFormat = "woff2";
      break;
    case "ttf":
      fontFormat = "truetype";
      break;
    case "otf":
      fontFormat = "opentype";
      break;
    default:
      // Handle the error as per your application's requirements
      console.error(
        "Error loading custom font: Invalid font format for URL:",
        fontUrl
      );
      return;
  }

  // Retry logic for font loading
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const myFont = new FontFace(
        fontName,
        `url(${fontUrl}) format('${fontFormat}')`,
        { display: "swap" }
      );

      await myFont.load();

      document.fonts.add(myFont);

      if (successFunction != null) {
        // wait 20ms to make sure the font is loaded
        setTimeout(successFunction, 20);
      }

      //console.log(`Font ${fontName} loaded successfully on attempt ${attempt}`);
      return;
    } catch (error) {
      console.error(
        `Attempt ${attempt} failed to load custom font. Font name: ${fontName}, Font URL: ${fontUrl} Error: ${error}`
      );

      if (attempt === maxRetries) {
        console.error(
          `Failed to load font ${fontName} after ${maxRetries} attempts`
        );
        throw error;
      } else {
        // Wait before retrying (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }
}

// Validate and test font URL accessibility
export async function validateFontURL(fontUrl, timeout = 5000) {
  if (!fontUrl || typeof fontUrl !== "string") {
    return { isValid: false, error: "Invalid URL format" };
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(fontUrl, {
      method: "HEAD",
      signal: controller.signal,
      mode: "cors",
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const contentType = response.headers.get("content-type");
      const contentLength = response.headers.get("content-length");

      // Check if it's a valid font content type
      const isValidFontType =
        contentType &&
        (contentType.includes("font/") ||
          contentType.includes("application/font") ||
          contentType.includes("application/x-font") ||
          contentType.includes("application/vnd.ms-opentype"));

      return {
        isValid: true,
        contentType,
        contentLength: contentLength ? parseInt(contentLength) : null,
        isFontType: isValidFontType,
      };
    } else {
      return {
        isValid: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
        status: response.status,
      };
    }
  } catch (error) {
    if (error.name === "AbortError") {
      return { isValid: false, error: "Request timeout" };
    }
    return { isValid: false, error: error.message };
  }
}

// Enhanced font loading with URL validation
export async function loadCustomFontWithValidation(
  fontName,
  fontUrl,
  successFunction = null,
  maxRetries = 3
) {
  // First validate the font URL
  const validation = await validateFontURL(fontUrl);

  if (!validation.isValid) {
    console.error(
      `Font URL validation failed for ${fontName}:`,
      validation.error
    );
    throw new Error(`Font URL validation failed: ${validation.error}`);
  }

  if (!validation.isFontType) {
    console.warn(
      `Font URL ${fontUrl} may not be a valid font file (content-type: ${validation.contentType})`
    );
  }

  // Proceed with font loading
  return loadCustomFont(fontName, fontUrl, successFunction, maxRetries);
}

export function isVideoPin(template) {
  // Check if there is a layer of type Video
  let isVideo = false;

  template.layers.forEach((layer) => {
    if (layer.type == "Video") {
      isVideo = true;
    }
  });

  return isVideo;
}

export function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
        a: 1,
      }
    : null;
}

export function rgbToHex(rgb) {
  // cast RGB values to int
  rgb.r = parseInt(rgb.r);
  rgb.g = parseInt(rgb.g);
  rgb.b = parseInt(rgb.b);
  rgb.a = parseFloat(rgb.a);

  return (
    "#" +
    ((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1)
  );
}

export function getActivePinterestProfile(authorizedSocialMediaProfiles) {
  let activePinterestProfileUsername = null;

  if (
    authorizedSocialMediaProfiles?.pinterest &&
    authorizedSocialMediaProfiles.pinterest.length > 0
  ) {
    authorizedSocialMediaProfiles.pinterest.forEach((profile) => {
      if (profile.isActive) {
        activePinterestProfileUsername = profile.username;
      }
    });
  }

  return activePinterestProfileUsername;
}

// Check if Pinterest is authenticated
export function isPinterestAuthenticated(authorizedSocialMediaProfiles) {
  return getActivePinterestProfile(authorizedSocialMediaProfiles) !== null;
}

export function getActiveXProfile(authorizedSocialMediaProfiles) {
  let activeXProfileUsername = null;

  if (
    authorizedSocialMediaProfiles?.x &&
    authorizedSocialMediaProfiles.x.length > 0
  ) {
    authorizedSocialMediaProfiles.x.forEach((profile) => {
      if (profile.isActive) {
        activeXProfileUsername = profile.username;
      }
    });
  }

  return activeXProfileUsername;
}

// Check if X is authenticated
export function isXAuthenticated(authorizedSocialMediaProfiles) {
  return getActiveXProfile(authorizedSocialMediaProfiles) !== null;
}

// Helper function to check if user has a connected and active profile for a specific platform
export function hasConnectedPlatformProfile(
  platform,
  authorizedSocialMediaProfiles,
  activeSocialMediaProfiles
) {
  if (!authorizedSocialMediaProfiles || !activeSocialMediaProfiles) {
    return false;
  }

  // Get active profile username for this platform
  const activeUsername = activeSocialMediaProfiles[platform];
  if (!activeUsername) {
    return false;
  }

  // Find the profile data
  const platformProfiles = authorizedSocialMediaProfiles[platform];
  if (!platformProfiles || platformProfiles.length === 0) {
    return false;
  }

  // Check if the active profile exists in the authorized profiles
  const activeProfile = platformProfiles.find(
    (profile) => profile.username === activeUsername
  );

  return activeProfile !== null && activeProfile !== undefined;
}

// Helper function to get the active profile for a specific platform
export function getActivePlatformProfile(
  platform,
  authorizedSocialMediaProfiles,
  activeSocialMediaProfiles
) {
  if (!authorizedSocialMediaProfiles || !activeSocialMediaProfiles) {
    return null;
  }

  // Get active profile username for this platform
  const activeUsername = activeSocialMediaProfiles[platform];
  if (!activeUsername) {
    return null;
  }

  // Find the profile data
  const platformProfiles = authorizedSocialMediaProfiles[platform];
  if (!platformProfiles) {
    return null;
  }

  return platformProfiles.find(
    (profile) => profile.username === activeUsername
  );
}

export function calculateBFTimeRemaining() {
  const now = dayjs();

  const endOfWeek = now.endOf("week"); // Calculate the end of the current week

  // Check if the countdown has reached 0
  if (now.isAfter(endOfWeek)) {
    // If so, reset the countdown to the end of the next week
    const nextWeekEnd = now.add(1, "week").endOf("week");
    return nextWeekEnd.from(now);
  }

  return endOfWeek.from(now);
}

export function userHasActivePlan(user) {
  if (
    user.credentials.planStatus == "Active" ||
    user.credentials.cancelSubscriptionDate > new Date().toISOString()
  ) {
    return true;
  } else {
    return false;
  }
}

// Helper function to check if user has an active subscription
export function hasActiveSubscription(userCredentials) {
  if (!userCredentials) return false;

  const { planStatus, cancelSubscriptionDate } = userCredentials;

  // Check if user has active or canceled status
  if (planStatus !== "Active" && planStatus !== "Canceled") {
    return false;
  }

  // If canceled, check if cancellation date is in the future
  if (planStatus === "Canceled" && cancelSubscriptionDate) {
    const cancelDate = new Date(cancelSubscriptionDate);
    const now = new Date();
    return cancelDate > now;
  }

  // If active, return true
  return planStatus === "Active";
}

export function trimDescriptionOnLastPuctuationMark(description) {
  // Trim the description to the last sentence-ending punctuation mark
  let descriptionParts = description.split(/(?<=[.!?])/);
  let lastIndex = descriptionParts.length - 1;

  // Find the index of the last non-empty part (in case the last part is an empty string)
  while (lastIndex >= 0 && !descriptionParts[lastIndex].trim()) {
    lastIndex--;
  }

  // Include all parts up to and including the last non-empty part
  let trimmedDesc = descriptionParts.slice(0, lastIndex + 1).join("");

  return trimmedDesc;
}

// Helper function to append URL to text when posting to social media
export function appendUrlToPostText(text, url, shouldIncludeURL) {
  if (!shouldIncludeURL || !url || !text) {
    return text;
  }

  // Clean the text and ensure it ends with proper punctuation
  let cleanText = text.trim();

  // If text doesn't end with punctuation, add a period
  if (!/[.!?]$/.test(cleanText)) {
    cleanText += ".";
  }

  // Add the URL with proper spacing
  return `${cleanText} ${url}`;
}

export function getSubscribeButtonText(plan, user) {
  if (!plan.planName.includes("Agency") && plan.freeTrialDays != "0") {
    if (user.authenticated == false) {
      return "Start free trial";
    } else if (user.authenticated && user.credentials.usedFreeTrial == false) {
      return "Start free trial";
    }
  }

  return "Start Plan";
}

// Debounce function to limit the rate of function execution
export function debounceFunction(func, timeout = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
}

// Helper function to generate PKCE code challenge using S256 method
async function generateCodeChallenge() {
  const codeVerifier = generateCodeVerifier();
  const hashBuffer = await sha256(codeVerifier);
  return base64URLEncode(hashBuffer);
}

// Generate a cryptographically random code verifier
function generateCodeVerifier() {
  const array = new Uint8Array(32);
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array);
  } else {
    // Fallback for older browsers
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256);
    }
  }
  return base64URLEncode(array);
}

// SHA256 hash function
function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest("SHA-256", data);
}

// Base64 URL encode function
function base64URLEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

// Clear specific OAuth state while preserving X session data
function clearXOAuthState() {
  try {
    // Only clear specific OAuth-related keys, not all X data
    // This preserves legitimate X session cookies that help with OAuth
    const oauthKeys = [
      "oauth_token",
      "oauth_verifier",
      "oauth_state",
      "x_oauth_state",
      "twitter_oauth_state",
      "pkce_verifier",
      "code_challenge",
      "oauth_callback_confirmed",
    ];

    // Clear specific OAuth keys from sessionStorage
    oauthKeys.forEach((key) => {
      sessionStorage.removeItem(key);
      // Also try variations with prefixes
      sessionStorage.removeItem(`x_${key}`);
      sessionStorage.removeItem(`twitter_${key}`);
    });

    // Clear specific OAuth keys from localStorage
    oauthKeys.forEach((key) => {
      localStorage.removeItem(key);
      localStorage.removeItem(`x_${key}`);
      localStorage.removeItem(`twitter_${key}`);
    });

    console.log(
      "Cleared specific OAuth state keys while preserving X session data"
    );
  } catch (error) {
    console.warn("Could not clear OAuth state:", error);
  }
}

// Frontend helper function to get default scheduling settings
// This mirrors the backend getDefaultSchedulingSettings() function
export const getDefaultSchedulingSettings = () => {
  return {
    platform: "Pinterest",
    socialMediaProfileUsername: "default",
    numberOfPostsPerDay: 1,
    windowOneTimes: [72000, 82800],
    windowTwoTimes: [7200, 14400],
    timezone: {
      label:
        "(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London",
      value: 0,
    },
    timeSlots: [
      7200, 8100, 9000, 9900, 10800, 11700, 12600, 13500, 72000, 72900, 73800,
      74700, 75600, 76500, 77400, 78300, 79200, 80100, 81000, 81900,
    ],
    postingIntervalInSeconds: 900,
    // Day selection for all platforms
    selectedDaysOfWeek: [1, 2, 3, 4, 5, 6, 0], // All days by default (0=Sunday, 1=Monday, etc.)
  };
};
